import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/services/api';
import { patchApplicationsCache } from '@/lib/recruiterCache';
import { applicationInboxPatchFromResponse, isApplicationUnread } from '@/lib/applicationInbox';
import type { Application } from '@/types';

export function useApplicationInbox(
  companyId: string | undefined,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const applyInboxPatch = useCallback((app: Application, patch: Application) => {
    if (!companyId) return;
    patchApplicationsCache(queryClient, companyId, [patch]);
  }, [companyId, queryClient]);

  const markViewed = useCallback(async (app: Application) => {
    if (!companyId || !userId || busyId === app.id || !isApplicationUnread(app)) return app;
    setBusyId(app.id);

    const optimistic = applicationInboxPatchFromResponse(app, {
      viewedAt: new Date().toISOString(),
      isUnread: false,
      inboxRevisionAt: app.inboxRevisionAt ?? app.appliedAt,
    });
    applyInboxPatch(app, optimistic);

    try {
      const updated = await api.applications.setViewed(
        companyId,
        app.positionId,
        app.id,
        true,
      );
      applyInboxPatch(app, updated);
      return updated;
    } catch {
      applyInboxPatch(app, app);
      throw new Error('mark_viewed_failed');
    } finally {
      setBusyId(null);
    }
  }, [companyId, userId, busyId, applyInboxPatch]);

  const markUnread = useCallback(async (app: Application) => {
    if (!companyId || !userId || busyId === app.id) return app;
    setBusyId(app.id);

    const optimistic = applicationInboxPatchFromResponse(app, {
      viewedAt: undefined,
      isUnread: true,
      inboxRevisionAt: app.inboxRevisionAt ?? app.appliedAt,
    });
    applyInboxPatch(app, optimistic);

    try {
      const updated = await api.applications.setViewed(
        companyId,
        app.positionId,
        app.id,
        false,
      );
      applyInboxPatch(app, updated);
      return updated;
    } catch {
      applyInboxPatch(app, app);
      throw new Error('mark_unread_failed');
    } finally {
      setBusyId(null);
    }
  }, [companyId, userId, busyId, applyInboxPatch]);

  const bulkMarkViewed = useCallback(async (apps: Application[]) => {
    if (!companyId || !userId || apps.length === 0) return;
    const unread = apps.filter(isApplicationUnread);
    if (unread.length === 0) return;

    const now = new Date().toISOString();
    const optimistic = unread.map((app) =>
      applicationInboxPatchFromResponse(app, {
        viewedAt: now,
        isUnread: false,
        inboxRevisionAt: app.inboxRevisionAt ?? app.appliedAt,
      }),
    );
    patchApplicationsCache(queryClient, companyId, optimistic);

    try {
      await api.applications.bulkMarkViewed(
        companyId,
        unread.map((a) => ({ applicationId: a.id, positionId: a.positionId })),
        true,
      );
    } catch {
      patchApplicationsCache(queryClient, companyId, unread);
      throw new Error('bulk_mark_viewed_failed');
    }
  }, [companyId, userId, queryClient]);

  const bulkMarkUnread = useCallback(async (apps: Application[]) => {
    if (!companyId || !userId || apps.length === 0) return;
    const read = apps.filter((app) => !isApplicationUnread(app));
    if (read.length === 0) return;

    const optimistic = read.map((app) =>
      applicationInboxPatchFromResponse(app, {
        viewedAt: undefined,
        isUnread: true,
        inboxRevisionAt: app.inboxRevisionAt ?? app.appliedAt,
      }),
    );
    patchApplicationsCache(queryClient, companyId, optimistic);

    try {
      await api.applications.bulkMarkViewed(
        companyId,
        read.map((a) => ({ applicationId: a.id, positionId: a.positionId })),
        false,
      );
    } catch {
      patchApplicationsCache(queryClient, companyId, read);
      throw new Error('bulk_mark_unread_failed');
    }
  }, [companyId, userId, queryClient]);

  return {
    markViewed,
    markUnread,
    bulkMarkViewed,
    bulkMarkUnread,
    isUnread: isApplicationUnread,
    isBusy: (appId: string) => busyId === appId,
  };
}
